import { Eye, EyeOff, Lock, Mail, UserIcon } from 'lucide-react'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import { AuthService } from '../utils/authService'

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    accountType: 'client' as 'admin' | 'accountant' | 'client',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Validate passwords match
      if (formData.password !== formData.passwordConfirm) {
        setError('Passwords do not match')
        return
      }

      // Validate UK phone number (must be 10 or 11 digits after +44)
      const digitsOnly = formData.phoneNumber.replace(/\s/g, '')
      if (!/^\d{10,11}$/.test(digitsOnly)) {
        setError('Please enter a valid UK phone number (10-11 digits)')
        return
      }

      const response = await AuthService.signup({
        ...formData,
        phoneNumber: `+44${digitsOnly}`,
      })

      if (response.statusCode === 201) {
        // Store user email for verification
        localStorage.setItem('verificationEmail', formData.email)
        navigate('/registering')
      } else {
        setError(response.response.message || 'Registration failed')
      }
    } catch (err: any) {
      // Extract the exact error message from API response
      let errorMessage = 'An error occurred during registration'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className='w-full max-w-[431px] mx-auto'>
        <div className='mb-8'>
          <p className='text-[16px] text-[#667085] text-start font-[400] leading-[24px]'>
            Welcome to BritFiler.
          </p>
          <h1 className='text-[36px] font-[700] text-[#181D27] mb-3 leading-[38px] text-start'>
            Let's set up your account!
          </h1>
        </div>

        {error && (
          <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm'>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='firstName'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              First Name
            </label>
            <div className='relative w-full'>
              <UserIcon className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='firstName'
                type='text'
                placeholder='John'
                value={formData.firstName}
                onChange={e =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
                required
              />
            </div>
          </div>

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='lastName'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Last Name
            </label>
            <div className='relative w-full'>
              <UserIcon className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='lastName'
                type='text'
                placeholder='Doe'
                value={formData.lastName}
                onChange={e =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
                required
              />
            </div>
          </div>

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='email'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Email
            </label>
            <div className='relative w-full'>
              <Mail className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='email'
                type='email'
                placeholder='olivia@untitledui.com'
                value={formData.email}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
              />
            </div>
          </div>

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='phoneNumber'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Phone Number
            </label>
            <div className='flex w-full'>
              <div className='flex items-center gap-1.5 px-3 h-[44px] border border-r-0 border-[#D0D5DD] rounded-l-[8px] bg-[#F9FAFB] shrink-0'>
                <span className='text-[14px]'>🇬🇧</span>
                <span className='text-[14px] font-medium text-[#344054]'>+44</span>
              </div>
              <input
                id='phoneNumber'
                type='tel'
                placeholder='7700 900000'
                value={formData.phoneNumber}
                onChange={e => {
                  const value = e.target.value.replace(/[^\d\s]/g, '')
                  setFormData({ ...formData, phoneNumber: value })
                }}
                className='w-full h-[44px] pl-[14px] pr-[14px] border border-[#D0D5DD] rounded-r-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
                required
              />
            </div>
          </div>

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='password'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Password
            </label>
            <div className='relative w-full'>
              <Lock className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                placeholder='••••••••'
                value={formData.password}
                onChange={e =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[44px] border border-[#D0D5DD] rounded-[8px] text-[16px] text-black placeholder:text-[#667085] outline-none bg-white'
                required
              />
              <button
                type='button'
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className='absolute right-[14px] top-1/2 -translate-y-1/2 text-[#667085] hover:text-[#344054]'
              >
                {showPassword ? (
                  <EyeOff className='w-5 h-5' />
                ) : (
                  <Eye className='w-5 h-5' />
                )}
              </button>
            </div>
          </div>

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='passwordConfirm'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Confirm Password
            </label>
            <div className='relative w-full'>
              <Lock className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='passwordConfirm'
                type={showPasswordConfirm ? 'text' : 'password'}
                placeholder='••••••••'
                value={formData.passwordConfirm}
                onChange={e =>
                  setFormData({ ...formData, passwordConfirm: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[44px] border border-[#D0D5DD] rounded-[8px] text-[16px] text-black placeholder:text-[#667085] outline-none bg-white'
                required
              />
              <button
                type='button'
                onClick={() => setShowPasswordConfirm(prev => !prev)}
                aria-label={
                  showPasswordConfirm ? 'Hide password' : 'Show password'
                }
                className='absolute right-[14px] top-1/2 -translate-y-1/2 text-[#667085] hover:text-[#344054]'
              >
                {showPasswordConfirm ? (
                  <EyeOff className='w-5 h-5' />
                ) : (
                  <Eye className='w-5 h-5' />
                )}
              </button>
            </div>
            <div className='flex flex-row w-full justify-start items-center mt-[14px] gap-2'>
              <input
                type='checkbox'
                className='border border-[#F04438] w-[18px] h-[18px] rounded-[10px] checked:bg-amber-600'
                required
              />
              <div className='text-[12px] text-[#334960] font-[600] text-center'>
                By clicking here you agree to our{' '}
                <Link
                  to='/terms'
                  className='hover:text-[#6941C6] underline'
                >
                  terms of use and privacy policy.
                </Link>
                .
              </div>
            </div>
          </div>

          <button
            type='submit'
            disabled={isLoading}
            className='w-full h-[44px] bg-[#001B38] cursor-pointer text-white text-[16px] font-semibold rounded-[8px] transition-colors duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>

          <div className='flex flex-row flex-nowrap items-center justify-center gap-2 whitespace-nowrap'>
            <span className='text-black text-[14px]'>Already a user?</span>
            <span className='w-[6px] h-[6px] bg-[#F04438] rounded-full shrink-0' />
            <Link
              to='/login'
              className='text-[#334960] font-semibold text-[14px] cursor-pointer hover:text-[#6941C6] hover:underline'
            >
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </AuthLayout>
  )
}
